/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import TransactionSearch from "./components/TransactionSearch";
import TransactionList from "./components/TransactionList";
import TransactionToolbar from "./components/TransactionToolbar";
import TransactionExportModal from "./components/TransactionExportModal";
import SweetAlert2 from "react-sweetalert2";
import { Box } from "@chakra-ui/react";
import moment from "moment";

interface Filters {
  [key: string]: any;
}

interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const initFrom = moment().subtract(1, "month");
const initTo = moment();
const initialDateRangeFilter = [initFrom.toDate(), initTo.toDate()];

const initialFilters: Filters = {
  from: initFrom,
  to: initTo,
  merchant_id: "",
  subaccount_id: "",
  agent_id: "",
  provider_id: "",
  provider_account_id: "",
  payout_id: "",
  asset: "",
  status: "",
};

const initialDateFiltersForExport: Filters = {
  from: initFrom.format("MMM D, YYYY 00:00"),
  to: initTo.format("MMM D, YYYY 23:59"),
};

const Transaction = ({ userPermissions }: { userPermissions: any }) => {
  const [swalProps, setSwalProps] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [isReadyToExport, setIsReadyToExport] = useState<boolean>(true);
  const [isOpenExportModal, setIsOpenExportModal] = useState<boolean>(false);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [dateFiltersForExport, setDateFiltersForExport] = useState(
    initialDateFiltersForExport
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [assetList, setAssetList] = useState<any>(null);

  const [selectedDates, setSelectedDates] = useState<Date[]>(initialDateRangeFilter);
  const agent_id = localStorage.getItem("agentId");
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const [dataFor, setDataFor] = useState<string>("all");
  // NEW: control filter toggle
  const [showFilters, setShowFilters] = useState(false);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  // Request ID to track the latest request and prevent stale updates
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    // Increment request ID to invalidate any previous requests
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      localStorage.removeItem("merchantIdToFilterTransaction");
      localStorage.removeItem("merchantNameToFilterTransaction");
      localStorage.removeItem("subaccountIdToFilterTransaction");
      localStorage.removeItem("subaccountNameToFilterTransaction");
      localStorage.removeItem("receiverIdToFilterTransaction");
      localStorage.removeItem("receiverAccountIdToFilterTransaction");
      localStorage.removeItem("providerIdToFilterTransaction");
      localStorage.removeItem("providerNameToFilterTransaction");
      localStorage.removeItem("providerAccountIdToFilterTransaction");
      localStorage.removeItem("providerAccountNameToFilterTransaction");
      localStorage.removeItem("agentIdToFilterTransaction");
      localStorage.removeItem("agentNameToFilterTransaction");
      setIsLoading(true);
      await fetchTransactions({ page: 0, limit: rowPerPage, ...filters }, false, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;

      if (userRole === "superadmin") {
        await fetchAssetsForSuperadmin(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else if (userRole === "merchant") {
        await fetchAssetsForMerchant(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else {
        if(userRole !== "agent") {
          await fetchAssets(signal);
          if (signal.aborted || requestIdRef.current !== currentRequestId) return;
        }
      }

      setIsLoading(false);
      setIsFilterLoading(false);
    };

    fetchData();
  }, []); // eslint-disable-line

  const fetchTransactions = async (
    payload: PayloadType = {},
    isReset = false,
    signal?: AbortSignal,
    requestId?: number
  ) => {
    try {
      const formattedFromDate = moment(
        !isReset ? selectedDates[0] : initFrom,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 00:00");
      const formattedToDate = moment(
        !isReset ? selectedDates[1] : initTo,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 23:59");

      const queryObject: PayloadType = {
        limit: payload?.limit,
        page: payload?.page + 1,
        from: formattedFromDate,
        to: formattedToDate,
        usertype: userRole === "superadmin" ? "a" : "m",
        ...(userRole === "agent" && { reseller_id: agent_id, display_mode: "agent" })
      };

      const merchantId = localStorage.getItem("merchantIdToFilterTransaction");
      const subaccountId = localStorage.getItem(
        "subaccountIdToFilterTransaction"
      );
      const providerId = localStorage.getItem("providerIdToFilterTransaction");
      const providerAccountId = localStorage.getItem(
        "providerAccountIdToFilterTransaction"
      );
      const agentId = localStorage.getItem("agentIdToFilterTransaction");

      if (payload?.status !== "" && payload?.status !== undefined) {
        queryObject.status = payload?.status;
      }

      if (payload?.asset !== "" && payload?.asset !== undefined) {
        queryObject.asset = payload?.asset;
      }

      if (payload?.payout_id !== "" && payload?.payout_id !== undefined) {
        queryObject.payout_id = payload?.payout_id;
      }

      if (userRole === 'superadmin') {
        delete queryObject.reseller_id;
        delete queryObject.provider_account_id;

        if (merchantId && merchantId !== "" && merchantId !== undefined) {
          queryObject.merchant_id = merchantId;
        }

        if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
          if (
            agentId &&
            agentId !== "" &&
            agentId !== undefined &&
            providerId &&
            providerId !== "" &&
            providerId !== undefined
          ) {
            queryObject.reseller_subaccount_id = subaccountId;
          } else {
            queryObject.subaccount_id = subaccountId;
          }
        }

        if (agentId && agentId !== "" && agentId !== undefined) {
          queryObject.reseller_id = agentId;
        }

        if (providerId && providerId !== "" && providerId !== undefined) {
          queryObject.provider_id = providerId;
        }

        if (
          providerId &&
          providerId !== "" &&
          providerId !== undefined &&
          providerAccountId &&
          providerAccountId !== "" &&
          providerAccountId !== undefined
        ) {
          delete queryObject.provider_id;
          queryObject.provider_account_id = providerAccountId;
        }

        if (
          agentId &&
          agentId !== "" &&
          agentId !== undefined &&
          providerId &&
          providerId !== "" &&
          providerId !== undefined
        ) {
          queryObject.data_for = dataFor;
        }
      }

      if (userRole === 'merchant') {
        if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
          queryObject.subaccount_id = subaccountId;
        }
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
        `/api/transaction/get-all-transactions?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted or a newer request was started
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.transactions;
      let totalTransactions = response.data.totalTransactions;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      setIsLoading(false);
      setRowCount(totalTransactions);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      // Don't update state if a newer request was started
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setIsLoading(false);
      // Clear all
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      ...filters
    };

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        ...filters
      };
      setPage(0);
      setPageSize(newValue.pageSize);
    } else {
      setPage(newValue.page);
    }

    fetchTransactions(newFilters);
  };

  const handleSearchSubmit = async () => {
    setIsReadyToExport(true);
    setIsLoading(true);
    setIsFilterLoading(true);
    setPage(0);
    await fetchTransactions({ page: 0, limit: rowPerPage, ...filters });
    setIsFilterLoading(false);
  };

  const handleSearchClear = async () => {
    setIsReadyToExport(true);
    localStorage.removeItem("merchantIdToFilterTransaction");
    localStorage.removeItem("merchantNameToFilterTransaction");
    localStorage.removeItem("subaccountIdToFilterTransaction");
    localStorage.removeItem("subaccountNameToFilterTransaction");
    localStorage.removeItem("receiverIdToFilterTransaction");
    localStorage.removeItem("receiverAccountIdToFilterTransaction");
    localStorage.removeItem("providerIdToFilterTransaction");
    localStorage.removeItem("providerNameToFilterTransaction");
    localStorage.removeItem("providerAccountIdToFilterTransaction");
    localStorage.removeItem("providerAccountNameToFilterTransaction");
    localStorage.removeItem("agentIdToFilterTransaction");
    localStorage.removeItem("agentNameToFilterTransaction");
    setIsLoading(true);
    setIsFilterLoading(true);
    setFilters(initialFilters);
    setPage(0);
    setSelectedDates(initialDateRangeFilter);
    setDateFiltersForExport(initialDateFiltersForExport);
    await fetchTransactions(
      { page: 0, limit: rowPerPage, ...initialFilters },
      true
    );
    setIsFilterLoading(false);
  };

  const fetchAssetsForSuperadmin = async (signal?: AbortSignal) => {
    try {
      let requestUrl = `/api/datasource/assets`;

      const response = await axios.get(
        process.env.REACT_APP_API_URL + requestUrl,
        { signal }
      );

      const data = response?.data;

      setAssetList(data);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching assets", error);
    }
  };

  const fetchAssetsForMerchant = async (signal?: AbortSignal) => {
    const merchantId = localStorage.getItem("merchantId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/merchant/get-merchant-by-id?merchantid=${merchantId}`,
        { signal }
      );

      const accountData = response.data.merchant?.payload;

      if (!accountData) {
        throw new Error('No merchant data found');
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            name: method.asset,
            crypto: method.crypto
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching merchant details:", error);
    }
  };

  const fetchAssets = async (signal?: AbortSignal) => {
    const subaccountId = localStorage.getItem("subaccountId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/subaccount/get-subaccount-by-id?subaccountid=${subaccountId}`,
        { signal }
      );

      const accountData = response.data.subaccount?.payload;

      if (!accountData) {
        throw new Error('No subaccount data found');
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            name: method.asset,
            decimal: method.decimal ?? 2,
            crypto: method.crypto
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching subaccount details:", error);
    }
  };

  const triggerReloadTransactionList = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    await fetchTransactions({
      page: page,
      limit: rowPerPage,
      ...filters,
    });
    setIsFilterLoading(false);
  };

  const handleExport = () => {
    showExportModal();
  };

  const showExportModal = () => {
    setIsOpenExportModal(true);
  };

  const closeExportModal = () => {
    setIsOpenExportModal(false);
  };

  return (
    <>
      <TransactionExportModal
        setSwalProps={setSwalProps}
        isOpen={isOpenExportModal}
        onClose={() => closeExportModal()}
        userPermissions={userPermissions}
        filters={filters}
        dataFor={dataFor}
        isReadyToExport={isReadyToExport}
        dateFiltersForExport={dateFiltersForExport}
        selectedDates={selectedDates}
      />

      <Box sx={{ position: "relative" }}>
        <TransactionToolbar
          isLoading={isLoading || isFilterLoading}
          onToggleFilters={() => setShowFilters((p) => !p)}
          onRefresh={triggerReloadTransactionList}
          onExport={handleExport}
        />
        <TransactionSearch
          showFilters={showFilters}
          userPermissions={userPermissions}
          isFilterLoading={isFilterLoading}
          filters={filters}
          setFilters={setFilters}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          handleSearchSubmit={handleSearchSubmit}
          handleSearchClear={handleSearchClear}
          assetList={assetList}
          dataFor={dataFor}
          setDataFor={setDataFor}
          setIsReadyToExport={setIsReadyToExport}
          setDateFiltersForExport={setDateFiltersForExport}
        />
        <TransactionList
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          tableData={isLoading ? [] : tableData}
          setPaginationModel={setPaginationModel}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default Transaction;
